import { type FC, type ReactNode } from 'react'
import classNames from 'classnames'
import { Button } from '../../../components/Button/Button'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import styles from './ScheduleSaveBar.module.scss'
import { type TimeSelectionMode } from './form/SetTimeSection'
import { type LinkShareState } from './useLinkShareState'
import { STALE_TITLE, type LinkNoun, type SaveBarNotice } from './saveBarNotice'
import { SaveBarNoticeStrip } from './SaveBarNoticeStrip'

interface ScheduleSaveBarProps {
  formId: string
  mode: TimeSelectionMode
  isValid: boolean
  isSubmitting: boolean
  linkState: LinkShareState
  linkNoun?: LinkNoun
  notice?: SaveBarNotice | null
  onDismissNotice: () => void
  incompleteReason?: string
  onContactSupport: () => void
  onCopyLink: () => void
  onUpdateBookingPage: () => void
  onPushToDraft: () => void
  pushToDraftDisabled?: boolean
  pushToDraftDisabledReason?: string
  submitDisabled?: boolean
  submitDisabledReason?: string
  submitDisabledTitle?: string
}

const LINK_NOUN_WORDING: Record<LinkNoun, { copyButton: string; copyTitle: string; ready: string }> = {
  booking: {
    copyButton: 'Copy booking link',
    copyTitle: "Copy your client's personal booking link to share it anywhere.",
    ready: 'Booking link ready to share',
  },
  reschedule: {
    copyButton: 'Copy reschedule link',
    copyTitle: 'Copy the reschedule link to share it anywhere — the first client to use it picks the new time.',
    ready: 'Reschedule link ready to share',
  },
}
const UPDATE_BOOKING_PAGE_TITLE = "Applies your latest changes to the client's booking page."
const PUSH_TO_DRAFT_TITLE = 'Creates a ready-to-send invitation draft in your email account.'
const SEND_INVITATION_EMAILS_TITLE = 'Zocks emails each client their personal booking link.'
const SEND_CALENDAR_INVITE_TITLE = 'Sends the calendar invite to your client.'

interface SaveBarActionButtonProps {
  icon: string
  title: string
  disabled: boolean
  onClick: () => void
  children: ReactNode
}

const SaveBarActionButton: FC<SaveBarActionButtonProps> = ({ icon, title, disabled, onClick, children }) => (
  <Tooltip title={disabled ? title : undefined}>
    <Button type='button' variant='dark' className='border zocks-border-color-gray-500' onClick={onClick} disabled={disabled} title={title}>
      <i className={`bi ${icon} me-1`} />
      {children}
    </Button>
  </Tooltip>
)

interface SubmitDisabledTooltipProps {
  title: string
  body?: string
}

const SubmitDisabledTooltip: FC<SubmitDisabledTooltipProps> = ({ title, body }) => (
  <>
    <div className='bold'>{title}</div>
    <div>{body}</div>
  </>
)

type SaveBarStatusTone = 'white' | 'warning'

const STATUS_CLASSNAMES: Record<SaveBarStatusTone, string> = {
  white: 'zocks-text-white',
  warning: 'zocks-text-warning-light',
}

interface SaveBarStatusProps {
  icon: string
  tone?: SaveBarStatusTone
  text: string
  children?: ReactNode
}

const SaveBarStatus: FC<SaveBarStatusProps> = ({ icon, tone = 'white', text, children }) => (
  <div className='d-flex align-items-center zocks-gap-0.75'>
    <i className={`bi ${icon} ${STATUS_CLASSNAMES[tone]}`} />
    <span className={STATUS_CLASSNAMES[tone]}>{text}</span>
    {children}
  </div>
)

interface LinkShareLeftAreaProps {
  linkState: LinkShareState
  linkNoun: LinkNoun
  isValid: boolean
  isSubmitting: boolean
  incompleteReason?: string
  onCopyLink: () => void
  onUpdateBookingPage: () => void
}

const LinkShareLeftArea: FC<LinkShareLeftAreaProps> = ({ linkState, linkNoun, isValid, isSubmitting, incompleteReason, onCopyLink, onUpdateBookingPage }) => {
  const wording = LINK_NOUN_WORDING[linkNoun]
  const copyBookingLinkButton = (
    <SaveBarActionButton icon='bi-copy' title={incompleteReason ?? wording.copyTitle} disabled={!isValid || isSubmitting} onClick={onCopyLink}>
      {wording.copyButton}
    </SaveBarActionButton>
  )

  switch (linkState) {
    case 'ready':
      return (
        <SaveBarStatus icon='bi-link-45deg' text={wording.ready}>
          {copyBookingLinkButton}
        </SaveBarStatus>
      )
    case 'stale':
      return (
        <SaveBarStatus icon='bi-exclamation-triangle-fill' tone='warning' text='Booking page still shows the previous settings'>
          <SaveBarActionButton icon='bi-arrow-repeat' title={UPDATE_BOOKING_PAGE_TITLE} disabled={isSubmitting} onClick={onUpdateBookingPage}>
            Update booking page
          </SaveBarActionButton>
        </SaveBarStatus>
      )
    default:
      return copyBookingLinkButton
  }
}

interface LinkShareActionsProps {
  formId: string
  linkState: LinkShareState
  isValid: boolean
  isSubmitting: boolean
  incompleteReason?: string
  onPushToDraft: () => void
  pushToDraftDisabled: boolean
  pushToDraftDisabledReason?: string
  submitDisabled: boolean
  submitDisabledReason?: string
  submitTooltipContent?: ReactNode
}

const LinkShareActions: FC<LinkShareActionsProps> = ({
  formId,
  linkState,
  isValid,
  isSubmitting,
  incompleteReason,
  onPushToDraft,
  pushToDraftDisabled,
  pushToDraftDisabledReason,
  submitDisabled,
  submitDisabledReason,
  submitTooltipContent,
}) => {
  const isStale = linkState === 'stale'
  const pushDisabled = !isValid || isSubmitting || pushToDraftDisabled || isStale
  const pushTitle = !isValid
    ? (incompleteReason ?? PUSH_TO_DRAFT_TITLE)
    : isStale
      ? STALE_TITLE
      : pushToDraftDisabled
        ? pushToDraftDisabledReason
        : PUSH_TO_DRAFT_TITLE
  const isSubmitDisabled = !isValid || isSubmitting || isStale || submitDisabled
  const submitTitle = !isValid
    ? (incompleteReason ?? SEND_INVITATION_EMAILS_TITLE)
    : submitDisabled
      ? submitDisabledReason
      : isStale
        ? STALE_TITLE
        : SEND_INVITATION_EMAILS_TITLE

  return (
    <div className='d-flex zocks-gap-0.75'>
      <Tooltip title={pushDisabled ? pushTitle : undefined}>
        <Button type='button' variant='dark' onClick={onPushToDraft} disabled={pushDisabled} title={pushTitle}>
          <i className='bi bi-inbox me-1' />
          Push to draft
        </Button>
      </Tooltip>
      <Tooltip title={isSubmitDisabled ? submitTitle : undefined} tooltipContent={isSubmitDisabled ? submitTooltipContent : undefined}>
        <Button type='submit' form={formId} variant='primary' className='border-0' disabled={isSubmitDisabled} loading={isSubmitting} title={submitTitle}>
          <i className='bi bi-send me-1' />
          Send invitation emails
        </Button>
      </Tooltip>
    </div>
  )
}

interface SpecificTimeSubmitProps {
  formId: string
  isValid: boolean
  isSubmitting: boolean
  incompleteReason?: string
  submitDisabled: boolean
  submitDisabledReason?: string
  submitTooltipContent?: ReactNode
}

const SpecificTimeSubmit: FC<SpecificTimeSubmitProps> = ({
  formId,
  isValid,
  isSubmitting,
  incompleteReason,
  submitDisabled,
  submitDisabledReason,
  submitTooltipContent,
}) => {
  const isDisabled = !isValid || isSubmitting || submitDisabled
  const title = !isValid ? (incompleteReason ?? SEND_CALENDAR_INVITE_TITLE) : submitDisabled ? submitDisabledReason : SEND_CALENDAR_INVITE_TITLE
  const showTooltip = isDisabled && !isSubmitting

  return (
    <Tooltip title={showTooltip ? title : undefined} tooltipContent={showTooltip ? submitTooltipContent : undefined}>
      <Button type='submit' form={formId} variant='primary' className='border-0' disabled={isDisabled} loading={isSubmitting} title={title}>
        <i className='bi bi-send me-1' />
        Send calendar invite
      </Button>
    </Tooltip>
  )
}

// Full-width dark action bar sticky to the bottom of the schedule flow, with an optional
// success/error notice strip attached above it. The "Let the client choose" mode adds the
// booking-link status and actions, which change with the link's share state; "Propose a
// specific time" only ever sends the invite. The notice persists until dismissed or until
// the next save bar action replaces it; the bar stays fully functional underneath.
export const ScheduleSaveBar: FC<ScheduleSaveBarProps> = ({
  formId,
  mode,
  isValid,
  isSubmitting,
  linkState,
  linkNoun = 'booking',
  notice = null,
  onDismissNotice,
  incompleteReason,
  onContactSupport,
  onCopyLink,
  onUpdateBookingPage,
  onPushToDraft,
  pushToDraftDisabled = false,
  pushToDraftDisabledReason,
  submitDisabled = false,
  submitDisabledReason,
  submitDisabledTitle,
}) => {
  const submitTooltipContent =
    isValid && submitDisabled && submitDisabledTitle != null ? <SubmitDisabledTooltip title={submitDisabledTitle} body={submitDisabledReason} /> : undefined

  return (
    <div
      className={classNames(
        'position-sticky mt-auto rounded-3 shadow',
        styles.bar,
        notice
          ? notice.variant === 'error'
            ? classNames('zocks-bg-color-danger-background', styles.errorFrame)
            : classNames('zocks-bg-color-success-background', styles.successFrame)
          : null,
      )}
    >
      {notice ? <SaveBarNoticeStrip notice={notice} onDismiss={onDismissNotice} onContactSupport={onContactSupport} /> : null}
      <div className='zocks-bg-color-gray-900 p-3 rounded-3'>
        <div className='d-flex align-items-center justify-content-between'>
          <div>
            {mode === 'client-chooses' ? (
              <LinkShareLeftArea
                linkState={linkState}
                linkNoun={linkNoun}
                isValid={isValid}
                isSubmitting={isSubmitting}
                incompleteReason={incompleteReason}
                onCopyLink={onCopyLink}
                onUpdateBookingPage={onUpdateBookingPage}
              />
            ) : null}
          </div>
          {mode === 'specific-time' ? (
            <SpecificTimeSubmit
              formId={formId}
              isValid={isValid}
              isSubmitting={isSubmitting}
              incompleteReason={incompleteReason}
              submitDisabled={submitDisabled}
              submitDisabledReason={submitDisabledReason}
              submitTooltipContent={submitTooltipContent}
            />
          ) : (
            <LinkShareActions
              formId={formId}
              linkState={linkState}
              isValid={isValid}
              isSubmitting={isSubmitting}
              incompleteReason={incompleteReason}
              onPushToDraft={onPushToDraft}
              pushToDraftDisabled={pushToDraftDisabled}
              pushToDraftDisabledReason={pushToDraftDisabledReason}
              submitDisabled={submitDisabled}
              submitDisabledReason={submitDisabledReason}
              submitTooltipContent={submitTooltipContent}
            />
          )}
        </div>
      </div>
    </div>
  )
}
